import { useCallback, useDeferredValue, useEffect, useRef, useState, type ReactNode } from 'react';
import type { EnrollmentItem, InputFieldOptions, SenderOption, StepDraft } from '@shared/flow/enrollment';
import { useDraftState } from '../../../hooks/useDraftState';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { WarningBadges } from './WarningBadges';
import { RecentSendsBar } from './RecentSendsBar';
import { contactDisplayIdentity } from './contactIdentity';
import { findConnectorField } from './connector-field';
import { itemBlockReason } from './itemBlockReason';
import { ItemFields } from './ItemFields';
import { isItemOverridden } from './queue-group-send';
import { useGongFlowStepsForced } from './gong-flow-queries';
import { StepOverrideEditor, type StepOverrideSnapshot } from './StepOverrideEditor';
import type { StagedStepCopy } from './state/staged-play-config';
import { useItemDraftReset } from './state/item-draft-reset';
import type { ItemAutosaveOwnership, ItemAutosavePatch, SendItemsOutcome } from './enroll-queue-types';

/**
 * Shared per-lead draft form (U5, buttonless-editor redesign U2) — the ONE
 * place sender/subject/copy fields render and save. Used inline under an
 * assigned row in the docked sidebar (`QueueBody`'s `AssignedRow`, including
 * inside `PlayQueueCard`) — D2 (single draft store): every mount calls the
 * same `onAutosave`/`onFlushSave`/`onSetStatus`, which all resolve to the
 * SAME `EnrollQueueContext.autosavePatch`/`flushAutosave`/`patchItem`
 * against the same `enrollment_item` row.
 *
 * U2: there is no separate draft-persist or readiness-toggle button. Edits autosave
 * (debounced, `onAutosave`) as the rep types; fields stay editable through
 * both `draft` and `ready` (only `sending`/readOnly locks them — see
 * `locked` below); ONE "Send" button flushes any pending autosave, PATCHes
 * `status: 'ready'`, then POSTs the send, in that strict order (behavior
 * spec #2). Client-visible send eligibility is `itemBlockReason` — the same
 * function `queue-group-send.ts`'s `deriveGroupSendability` reuses for
 * `PlayQueueCard`'s per-row/per-group sendability, so the copy can't drift
 * between the row editor and the card.
 *

 * No `RichHtmlField`/`dangerouslySetInnerHTML` here: the only live play type
 * today is `event_invite`, whose copy is sent as literal text with
 * `{{Firstname}}`/`{{Account}}` placeholders (Clay merges them downstream)
 * — never rendered as HTML in this build. A future Gong-Sequence-style HTML
 * copy mode (the prototype's `copyMode==='html'`) would need `RichHtmlField`
 * then, not now.
 *
 * `useDraftState` (CLAUDE.md "Draft State Under SSE"; also called out
 * explicitly in docs/flow/flow-queuing-implementation-plan.md's UI surface
 * section) keyed on `item.id`: server refetches after a save (query
 * invalidation) never wipe an in-progress, unsaved edit — the render-phase
 * guard only reseeds when the item identity itself changes.
 */

export interface EnrollmentItemDraft {
  sender: string;
  subject: string;
  body: string;
}

/** The assigned play's campaign copy — the prefill seed for unset drafts. */
export interface CopyPrefill {
  subject: string | null;
  body: string | null;
}

/**
 * Prefill semantics = resolveCopy (src/services/flow/enroll-service.ts):
 * rep input > campaign. An unset draft seeds from the play's campaign copy
 * (invite_subject/invite_body on PlayOption, from deriveLivePlays) — the
 * same value the send would fall back to server-side, so what the rep sees
 * IS what sends. (Supersedes the pre-merge OQ3 'n/a' placeholder — the
 * Play Manager is on this branch now, so real campaign copy is available.)
 *
 * `senderPrefill` (design-fidelity plan D12 point 4) is the sender rung's
 * twin of `copyPrefill`, with ONE deliberate asymmetry: it BEATS the item's
 * own `sender` rather than falling behind it. That is not an inconsistency —
 * the caller has already applied the precedence. It passes the play's staged
 * global sender only for a row that is NOT in the touched set (a row whose
 * sender the rep hand-picked), and `null` otherwise, so "the item's own value
 * wins" is expressed by the caller handing over `null` instead of by the
 * order here. Copy can't work that way: `subject_draft !== null` IS the
 * "overridden" signal, whereas a seeded `sender` is indistinguishable from a
 * hand-picked one (see `seedSenderForPlay`'s own doc comment), which is
 * exactly why the touched set exists.
 */
export function seedEnrollmentItemDraft(
  item: EnrollmentItem,
  prefill?: CopyPrefill,
  senderPrefill?: string | null,
): EnrollmentItemDraft {
  return {
    sender: senderPrefill ?? item.sender ?? '',
    subject: item.subject_draft ?? prefill?.subject ?? '',
    body: item.body_draft ?? prefill?.body ?? '',
  };
}

/**
 * Stable empty default for `inputOptions` — a `[]` literal in the parameter
 * default allocates a NEW array every render, which would re-run the
 * default-selection effect below on every parent re-render (and this editor
 * re-renders on every unrelated SSE event, CLAUDE.md "Draft State Under SSE").
 */
const NO_INPUT_OPTIONS: InputFieldOptions[] = [];

/**
 * Warm Intro plan U11 (R9): the editor mount REPLACES the amber
 * `recent_invite` pill with `RecentSendsBar` below — `QueueBody`'s row mount
 * is untouched and keeps every pill. Module-level so the array identity is
 * stable across renders (same rationale as `NO_INPUT_OPTIONS`).
 *
 * `'cooloff'` is deliberately absent: it was a pure duplicate of
 * `recent_invite` — see `WarningBadges.tsx`'s matching `suppressKinds` doc
 * comment.
 */
const SUPPRESSED_WARNING_KINDS: Array<'recent_invite'> = ['recent_invite'];

export interface EnrollmentItemEditorProps {
  item: EnrollmentItem;
  /** Edit lock: fields and pickers are disabled. Does NOT gate Send (see `sendReadOnly`). */
  readOnly: boolean;
  /** Send gate, distinct from `readOnly`. Defaults to `readOnly` when omitted. */
  sendReadOnly?: boolean;
  /** Sender picker options — the Playhouse-mapped list (GET /api/flow/queue/senders). */
  senders: SenderOption[];
  /**
   * Per-ITEM, server-resolved field options (Warm Intro plan U8) — the
   * `GET /api/flow/queue/items/:id/input-options` payload, threaded from
   * `EnrollQueueContext.inputOptionsFor(itemId)`. One entry per input field
   * whose `ActionInputField.optionsSource` says the server resolves its
   * options per contact; today the only one is warm_intro's connector list.
   *
   * Its own INDEPENDENT render branch: unrelated to `hasEditableCopy`
   * (warm_intro resolves subject/body server-side yet still needs this
   * picker) and unrelated to the sender select. Empty (the default) for every
   * other play type, which renders nothing.
   */
  inputOptions?: InputFieldOptions[];
  /**
   * Persist a connector-picker selection. Fires on rep change AND on the
   * auto-default (see the effect below) — the DB is the single source of
   * truth, because the mark-ready gate that requires a selection runs
   * SERVER-side (`patchQueueItem`'s `WarmIntroRequiredError`) and can only
   * see a persisted value. `null` clears it.
   *
   * `fieldKey` is passed through so the ONE call site that maps a field key
   * to its `ItemPatch` column (the dock's send handlers) owns that mapping; the
   * editor stays agnostic about which column a given options field persists
   * into.
   */
  onSelectInputOption?: (itemId: number, fieldKey: string, persistsTo: InputFieldOptions['persistsTo'], value: string | null) => Promise<void>;
  /**
   * Fix 5a (MAJOR bd finding): called once on mount for a draft warm_intro
   * item (proxied by `inputOptions.length > 0` — see the doc comment on
   * `inputOptions` above: today that's the only play type with a non-empty
   * options list) to force-refresh the cached connector list if it's older
   * than the mirror's real hourly sync cadence. Optional so a caller with no
   * staleness concept (or a test that doesn't care) can render this editor
   * without it — a no-op then, not a crash.
   */
  onRefreshInputOptionsIfStale?: (itemId: number) => void;
  /** The assigned play's campaign copy (prefill for unset drafts). */
  copyPrefill?: CopyPrefill;
  /**
   * D12 point 4 — the play's STAGED global sender when this row should follow
   * it, `null`/omitted when it must not (the row is in the rep-local
   * `senderTouched` set, or the play has nothing staged). See
   * `seedEnrollmentItemDraft` for why this rung BEATS `item.sender` rather
   * than falling behind it, and the live-follow effect below for why a
   * non-null value also has to reach an ALREADY-OPEN editor.
   */
  senderPrefill?: string | null;
  /**
   * D12 point 4 — "the rep hand-picked this row's sender", so a staged
   * global sender must never re-follow it (`markSenderTouched`). Called from
   * the Sender field's change handler ONLY: the single-option auto-default
   * effect deliberately does not call it (that is a heal for a play with one
   * possible answer, not a pick, and such a play has nothing to diverge
   * from). Optional so a caller with no staging concept can mount the editor
   * unchanged.
   */
  onSenderTouched?: (itemId: number) => void;
  /**
   * Whether the assigned play has rep-editable copy (Subject/Copy). Driven by
   * the play's connector inputSchema (PlayOption.has_editable_copy). Defaults
   * to true so an unmatched/stale play still shows the fields. A play with no
   * `input.*` fields (e.g. generate_message — everything resolved server-side)
   * passes false, and the editor collapses to just Sender + the send actions.
   */
  hasEditableCopy?: boolean;
  /**
   * Whether the assigned play's Subject/Copy is genuinely REQUIRED to send, with no
   * blank-falls-back-to-template semantics (`PlayOption.copy_required`'s doc
   * comment). Defaults to `false` (no star) so an unmatched/stale play never
   * guesses a field is required — the safe default matches
   * `hasEditableCopy`'s own "collapse to nothing shown" posture for that
   * case. Only meaningful alongside `hasEditableCopy: true`.
   */
  copyRequired?: boolean;
  /**
   * U2 — schedules a debounced draft-field PATCH (replaces the removed
   * explicit draft-persist button). Fire-and-forget: called from the dirty
   * effect below AND — since D12 Codex fix #1 — synchronously from the Sender
   * field's own change handler; never awaited. Resolves to
   * `EnrollQueueContext.autosavePatch`.
   *
   * `patch` is SPARSE (only the halves that carry a local write) and `owned`
   * says, per half, whether the value is the rep's own deliberate choice —
   * see `ItemAutosavePatch`/`ItemAutosaveOwnership` for why both matter.
   */
  onAutosave: (itemId: number, patch: ItemAutosavePatch, owned: ItemAutosaveOwnership) => void;
  /**
   * U2 — immediately fires any autosave pending for `itemId`, awaited by
   * Send BEFORE the mark-ready PATCH (behavior spec #2's ordering
   * requirement). Resolves to `EnrollQueueContext.flushAutosave`.
   */
  onFlushSave: (itemId: number) => Promise<void>;
  onSetStatus: (
    itemId: number,
    status: 'draft' | 'ready',
    patch: { sender: string; subject_draft: string; body_draft: string },
  ) => Promise<void>;
  /**
   * POST /api/flow/queue/send for exactly this one item (D7 "Send 1").
   * Optional so a caller that doesn't have a send path yet (there is none
   * left as of this bead) can still render the editor read-only.
   *
   * D12 Codex round-2 fix #6 — resolves with the ADMISSION RESULT
   * (`EnrollQueueContext.sendItems`, threaded through unchanged), so
   * `handleSend` can compensate a row the server SKIPPED. A skipped item is a
   * successful HTTP response, so it never reached the `catch` that rolls back
   * a failure, and its committed materialization stayed on a row that never
   * sent.
   *
   * `| void` in the return union, not a bare `SendItemsOutcome`: this prop is
   * already optional for a caller with no send path, and the same latitude
   * applies to a caller that has one but reports no outcome (every leaf test
   * that mounts this editor with a bare `vi.fn()`). `handleSend` treats an
   * absent outcome as "nothing to compensate", which is the pre-fix behavior
   * — so a partial mount degrades to the old semantics instead of crashing.
   * Every REAL mount threads `EnrollQueueContext.sendItems` through, so the
   * outcome is always present in production.
   */
  onSend?: (itemId: number) => Promise<SendItemsOutcome | void>;
  /**
   * A send is in flight (B1 client belt). Disables Send so a double-click /
   * second surface can't fire a second `POST /send` before the item leaves
   * the queue. The server's atomic claim is the real guard; this is UX.
   */
  sending?: boolean;
  /**
   * Optional recipient-preview slot. Receives the live (possibly unsaved)
   * draft so the preview updates as the rep types.
   *
   * Currently UNUSED: its only caller was the full-screen review, retired by
   * flow-contacts-bulk-send U4c. Left in place (rather than removed with the
   * surface) because the dock's own card is the natural home for a preview
   * slot if one is wanted — deciding that is design work, not rebase work.
   */
  renderPreview?: (draft: EnrollmentItemDraft) => ReactNode;
  /**
   * U13 — the assigned play's Gong flow id (`item.play_instance_ref`) when
   * `play_type_slug === GONG_FLOW_PLAY_TYPE_SLUG`; `null`/omitted for every
   * other play type. Non-null mounts the step-override editor + cool-off
   * checkbox + owner note beneath the shared field cluster. The caller
   * (`QueueBody`, which already resolves `plays`) owns this check — this
   * editor stays agnostic about `PlayOption` shape.
   */
  gongFlowId?: string | null;
  /**
   * U9 (F2c) — the resolved play type's `ui.body_format` capability
   * (`PlayUiCapabilitiesSchema`, `src/shared/flow/play-catalog.ts`): `'rich'`
   * mounts `RichTextBodyField`'s formatting toolbar for the Gong step body
   * editor below; `'plain'` (default) keeps the bare `RichHtmlField`.
   * Same "caller resolves `PlayOption`, this editor stays agnostic" split as
   * `gongFlowId` — only meaningful together with a non-null `gongFlowId`,
   * since `StepOverrideEditor` is the only current consumer.
   */
  bodyFormat?: 'plain' | 'rich';
  /** Basic save-and-send (plan 2026-08-19-004) — records one step's full effective snapshot in the LOCAL draft store; no network (`EnrollQueueContext.editStepOverride`). */
  onEditStepOverride?: (itemId: number, stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /**
   * "Use flow default" — local intent until Send
   * (`EnrollQueueContext.resetStepOverrideToDefault`). Finding #3: no
   * `hasPersistedOverride` argument — the context decides whether a DELETE
   * is needed from data it owns; a render-derived boolean was stale in the
   * PUT-landed-but-not-yet-refetched window.
   */
  onResetStepOverride?: (itemId: number, stepNumber: number, stepType: string) => void;
  /** The store's unsaved entry for one step, or null (`EnrollQueueContext.stepOverrideDraftFor`). */
  stepOverrideDraftFor?: (itemId: number, stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  /** The last send-time save failure for one step, or null (`EnrollQueueContext.stepSaveErrorFor`). */
  stepSaveErrorFor?: (itemId: number, stepNumber: number) => string | null;
  /**
   * Typing-lag fix (2026-08-20) — the `useSyncExternalStore` subscribe half
   * for one step's entry (`EnrollQueueContext.subscribeStepOverrides`).
   * Required together with the two props above (same "no partial mount"
   * rule as the rest of this cohesive step-override set) — without it,
   * `StepOverrideEditor`'s rows have no way to re-render on their own edits.
   */
  subscribeStepOverrides?: (itemId: number, stepNumber: number, onChange: () => void) => () => void;
  /**
   * The Send handler's save phase (`EnrollQueueContext.saveStepOverrides`):
   * PUTs/DELETEs every locally-edited step of this item, awaited AFTER
   * `onFlushSave` and BEFORE the `ready` status flip. The ordering is
   * load-bearing: the server's `assertOwnedEditableItem`
   * (`src/services/flow/step-drafts.ts:96-107`) rejects a step-override
   * write with `STEP_DRAFT_ITEM_IMMUTABLE` once the item is
   * `sending`/`submitted`, so a save moved later would both lose the rep's
   * copy and show "Failed to save — retry" on an already-sent item. A
   * rejection aborts the send.
   *
   * REQUIRED (finding #7), unlike the other step props (which gate only the
   * gong_flow section's mount): this one gates the save-before-send step of
   * EVERY Send, and an editor mounted without it would silently skip that
   * gate. Non-gong_flow callers pass it too — `saveStepOverrides` is a
   * cheap no-op for an item with no local step entries.
   */
  onSaveStepOverrides: (itemId: number) => Promise<void>;
  /**
   * U8 (bulk step-override editor, plan 2026-09-08-002 Part 2, v3 rewrite) —
   * pure pass-through to `GongFlowStepsSection` → `StepOverrideEditor`'s
   * matching optional prop; this editor binds nothing further. Omitted
   * (every non-gong_flow mount, and every pre-existing test) →
   * `StepOverrideEditor` renders byte-identically to today.
   */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  /** U13 — the cool-off checkbox's write (`EnrollQueueContext.patchItem({ cool_off_override })`). */
  onToggleCoolOff?: (itemId: number, value: boolean) => Promise<void>;
  /**
   * Adversarial follow-up (findings 1/2/5) — the CONTEXT-level per-item
   * send-pipeline lock (`EnrollQueueContext.isItemBusy(item.id)`), threaded
   * down so THIS mounted instance locks its fields and actions whenever ANY
   * surface is mid-pipeline for the item. The instance-local `sendInProgress`
   * below can't do that job: a collapse+reopen, or a different trigger
   * surface (per-row vs group Send), started with a fresh unlocked editor.
   * Required — a mount without it silently reintroduces that bug class.
   */
  itemBusy: boolean;
  /**
   * Claims the pipeline lock (`EnrollQueueContext.beginSendPipeline`) before
   * Send's first await; Send refuses to start when it returns false (some
   * surface already has a pipeline running for this item).
   */
  onBeginSendPipeline: (itemIds: number[]) => boolean;
  /** Releases the pipeline lock in Send's `finally` (`EnrollQueueContext.endSendPipeline`). */
  onEndSendPipeline: (itemIds: number[]) => void;
  /**
   * U2 (V2 rail, D25, additive-only) — hides the built-in "Send 1"/"Request 1"
   * button block (the `:1341` mount) when a caller renders its own send
   * affordance instead (the V2 rail's `LeadRow` action). Defaults to `false`
   * so every existing V1 call site, which omits this prop, renders
   * byte-identically to before.
   */
  hideSendButton?: boolean;
  /**
   * U7 (V2 rail-glaze-parity, additive-only) — hides this editor's own
   * `WarningBadges` mount (the `:1235` render) when a caller already
   * surfaces the item's warnings elsewhere (the V2 rail's `LeadRow` meta
   * strip mounts `WarningBadges` directly, so the editor's copy would
   * double the same pills). Defaults to `false` so every existing V1 call
   * site, which omits this prop, renders byte-identically to before — see
   * `EnrollQueue.send-integration.test.tsx`'s two-instance assertion.
   */
  hideWarningBadges?: boolean;
  /**
   * U2 (V2 rail, D25, additive-only) — lets a caller substitute its own
   * gong-flow step-editor UI for the built-in `GongFlowStepsSection` mount
   * (the `:1286` guard), receiving the same data that mount already resolves
   * (`itemId`, `gongFlowId`, `bodyFormat`, `locked`, `lockedTitle`,
   * `batchStepCopy`). Omitted (every existing V1 call site) →
   * `GongFlowStepsSection` renders byte-identically to before.
   */
  stepsSection?: (args: {
    itemId: number;
    gongFlowId: string;
    bodyFormat: 'plain' | 'rich';
    locked: boolean;
    lockedTitle?: string;
    batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  }) => ReactNode;
}

/**
 * U13 — Gong flow steps section: fetches `GET /api/flow/gong/flows/:flowId/
 * steps` and, once resolved, mounts `StepOverrideEditor` + the cool-off
 * checkbox + the "Enrolls as you" owner note beneath the shared field
 * cluster (locked prototype's `gongStepsHtml`,
 * `docs/flow/gong-flow-play-and-queue-redesign-prototype.html:572-605`).
 * A steps-endpoint failure renders a "steps unavailable" note and stops
 * there — it does NOT propagate up and break Sender/Subject/Copy/Send,
 * which is why this is its own self-contained subtree rather than inline
 * in `EnrollmentItemEditor`.
 */
function GongFlowStepsSection({
  itemId,
  flowId,
  overrides,
  locked,
  lockedTitle,
  onEditStepOverride,
  onResetStepOverride,
  stepOverrideDraftFor,
  stepSaveErrorFor,
  subscribeStepOverrides,
  batchStepCopy,
  coolOffOverride,
  onToggleCoolOff,
  actingRepEmail,
  bodyFormat = 'plain',
}: {
  itemId: number;
  flowId: string;
  overrides: StepDraft[];
  locked: boolean;
  lockedTitle?: string;
  onEditStepOverride: (itemId: number, stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  onResetStepOverride: (itemId: number, stepNumber: number, stepType: string) => void;
  stepOverrideDraftFor: (itemId: number, stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  stepSaveErrorFor: (itemId: number, stepNumber: number) => string | null;
  subscribeStepOverrides: (itemId: number, stepNumber: number, onChange: () => void) => () => void;
  /** U8 — see `EnrollmentItemEditorProps.batchStepCopy`'s doc comment; handed straight to `StepOverrideEditor`, unbound further. */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  coolOffOverride: boolean;
  onToggleCoolOff: (itemId: number, value: boolean) => Promise<void>;
  actingRepEmail: string | null;
  /** U9 (F2c) — see `EnrollmentItemEditorProps.bodyFormat`'s doc comment. */
  bodyFormat?: 'plain' | 'rich';
}) {
  // R5a/U5: live step data at editor-mount time — forced variant bypasses
  // the never-invalidated steps cache (see `gong-flow-queries.ts` doc
  // comment) and refetches on every mount, not just the first.
  const stepsQuery = useGongFlowStepsForced(flowId);

  if (stepsQuery.isLoading) return null;
  if (stepsQuery.isError || !stepsQuery.data) {
    return (
      <div
        role="alert"
        className="mt-2 rounded-md bg-bg-red-light px-2 py-1.5 text-xs text-content-danger"
      >
        Flow steps unavailable — try again shortly.
      </div>
    );
  }

  const { steps, flow_owner_name: flowOwnerName } = stepsQuery.data;

  return (
    <div className="mt-2">
      <StepOverrideEditor
        itemId={itemId}
        steps={steps}
        overrides={overrides}
        disabled={locked}
        disabledTitle={lockedTitle}
        onChangeOverride={(stepNumber, stepType, values) => onEditStepOverride(itemId, stepNumber, stepType, values)}
        onResetOverride={(stepNumber, stepType) => onResetStepOverride(itemId, stepNumber, stepType)}
        localDraftFor={stepNumber => stepOverrideDraftFor(itemId, stepNumber)}
        saveErrorFor={stepNumber => stepSaveErrorFor(itemId, stepNumber)}
        subscribeStep={(stepNumber, onChange) => subscribeStepOverrides(itemId, stepNumber, onChange)}
        batchStepCopy={batchStepCopy}
        bodyFormat={bodyFormat}
      />
      <div className="mt-2 flex items-start gap-1.5 text-xs">
        <input
          type="checkbox"
          id={`cooloff-${itemId}`}
          checked={coolOffOverride}
          disabled={locked}
          title={lockedTitle}
          onChange={e => { void onToggleCoolOff(itemId, e.target.checked); }}
          className="mt-0.5"
        />
        {/* Exact decided copy (spec handoff 8/19) — kept as ONE literal
            string (not split across a styled <span>) so it stays
            byte-for-byte greppable/diffable against the decision record. */}
        <label htmlFor={`cooloff-${itemId}`} className="cursor-pointer text-content-secondary">
          Override cool-off — enroll even though this contact recently finished or left another flow.
        </label>
      </div>
      <div className="mt-2 text-xs text-content-tertiary">
        Enrolls as <strong className="text-content-secondary">you</strong>
        {actingRepEmail ? ` (${actingRepEmail})` : ''} — you own the flow to-dos in Gong.
        {flowOwnerName && ` Flow owner: ${flowOwnerName}.`}
      </div>
    </div>
  );
}

export function EnrollmentItemEditor({ item, readOnly, sendReadOnly, senders, inputOptions = NO_INPUT_OPTIONS, onSelectInputOption, onRefreshInputOptionsIfStale, copyPrefill, senderPrefill = null, onSenderTouched, hasEditableCopy = true, copyRequired = false, onAutosave, onFlushSave, onSetStatus, onSend, sending, renderPreview, gongFlowId = null, bodyFormat = 'plain', onEditStepOverride, onResetStepOverride, stepOverrideDraftFor, stepSaveErrorFor, subscribeStepOverrides, onSaveStepOverrides, batchStepCopy, onToggleCoolOff, itemBusy, onBeginSendPipeline, onEndSendPipeline }: EnrollmentItemEditorProps) {
  // U2 (V2 rail, D25, additive-only) — read the two new optional props via
  // `arguments[0]` rather than adding them to the destructuring parameter
  // above: any edit to that one (pre-existing, very long) parameter line
  // would rewrite it wholesale in a line-based diff, which is exactly what
  // this unit's "additions only" verification gate
  // (`git diff … EnrollmentItemEditor.tsx | grep '^-' …` must print nothing)
  // forbids. `EnrollmentItemEditor` is a plain `function` declaration (not
  // an arrow function), so `arguments` is available even though its single
  // parameter is destructured.
  const { hideSendButton = false, hideWarningBadges = false, stepsSection } = arguments[0] as EnrollmentItemEditorProps;
  // Read-only identity context (same precedent as `PlayPicker.tsx`'s
  // `useUserControlsContext()` call) — the owner note's "you" needs the
  // acting rep's own email, which no caller currently threads as a prop.
  const { email: actingRepEmail } = useUserControlsContext();
  /**
   * D12 Codex round-2 fix #2 — the editor's DRAFT IDENTITY, which is the
   * item id PLUS how many times this item has been reset to base.
   *
   * "Reset to base copy" discards the item's context-level autosave entry and
   * PATCHes the row back to base-following, but it had no way to reach THIS
   * component's own state, and every guard here works against it:
   * `useDraftState` applies a seed exactly once per entity identity and
   * `item.id` does not change on a reset; the copy live-follow effect is
   * gated on `copyEdited`, whose ref was still set; the sender live-follow
   * effect needs a non-null `senderPrefill`, which a play with nothing staged
   * does not have. So an open editor kept DISPLAYING the discarded subject/
   * body/sender, and the rep's next keystroke (or Send) re-persisted exactly
   * what Reset had thrown away.
   *
   * Folding the generation into the key fixes it through the mechanisms that
   * already exist rather than adding a reset effect: `useDraftState`'s
   * documented render-phase guard reseeds from the current seed the moment
   * `appliedKey !== seedKey`, and the four local-write refs below — which
   * store this key rather than a boolean, precisely so a row switch
   * invalidates them BY CONSTRUCTION — are invalidated by a bump for free.
   * No mount-lifetime flag, so nothing for StrictMode's setup→cleanup→setup
   * replay to leave stuck (CLAUDE.md "StrictMode-Resilient Effects").
   *
   * The generation is bumped only AFTER the reset's PATCH resolved and the
   * queue read was refetched (`EnrollQueueContext.resetItemToBase`), so the
   * seed this reseeds from is the post-reset row.
   */
  const draftResetGeneration = useItemDraftReset(item.id);
  const draftKey = `${item.id}:${draftResetGeneration}`;
  const form = useDraftState<EnrollmentItemDraft>(seedEnrollmentItemDraft(item, copyPrefill, senderPrefill), draftKey);
  const draft = form.draft ?? seedEnrollmentItemDraft(item, copyPrefill, senderPrefill);

  /**
   * ── The four independent local-write facts about THIS item ───────────────
   *
   * D12 Codex fix (findings #1–#3). There used to be ONE ref
   * (`repEditedItemRef`), set by every field handler alike, doing three
   * different jobs at once: gating the copy live-follow effect, deciding what
   * the autosave writes, and standing in for "the rep owns this value" at
   * send-time materialization. Collapsing "the rep picked a sender" and "the
   * rep typed into copy" into one flag is the architectural root cause behind
   * all three findings — most visibly finding #3, where merely picking a
   * sender froze whatever base copy happened to be showing into a permanent
   * per-item override.
   *
   * They are now two orthogonal questions, asked twice each:
   *
   *  - WRITE (`senderWrite`/`copyWrite`) — "does this half hold a local value
   *    that has to reach the server?" True for the rep's own edits AND for
   *    the two auto-appliers below (the single-option sender heal, the
   *    connector-driven copy prefill), because those genuinely do need
   *    persisting. A half with no local write is OMITTED from the autosave
   *    patch entirely, which is what stops a sender pick from ever writing a
   *    `subject_draft` (finding #3) and a copy edit from ever writing a
   *    `sender` (finding #2's producer half).
   *
   *  - OWN (`senderPicked`/`copyEdited`) — "did the REP deliberately choose
   *    this half?" A strict subset of WRITE: the two auto-appliers are
   *    excluded, because a heal is not a choice. This is the flag that rides
   *    along on the autosave entry as `ItemAutosaveOwnership` and decides,
   *    per half, whether send-time materialization may replace the value with
   *    the play's staged one. `copyEdited` is additionally what gates the copy
   *    live-follow effect below — and NOTHING about the sender touches that
   *    gate any more.
   *
   * All four store the DRAFT KEY rather than a boolean, so switching the
   * editor to another row makes them false BY CONSTRUCTION: no mount-lifetime
   * flag for StrictMode's setup→cleanup→setup replay to leave stuck, and no
   * reset effect to keep in sync (CLAUDE.md "StrictMode-Resilient Effects" —
   * the rule's subject is refs that DO need a reset; this shape needs none).
   *
   * D12 Codex round-2 fix #2: the key is `${item.id}:${resetGeneration}`, not
   * the bare item id, so a completed "Reset to base copy" invalidates all
   * four the same way a row switch does — see `draftKey` above. Without this,
   * `copyEdited` stayed true across a reset and blocked the copy live-follow
   * effect from ever reseeding the row it had just discarded.
   *
   * Written SYNCHRONOUSLY in the handlers below, ahead of any state update or
   * network call, because the very next render (and, per finding #1, anything
   * reading the shared autosave entry in the SAME tick) has to see them. They
   * have to exist separately from `item.subject_draft !== null` /
   * `item.sender` because the autosave PATCH is debounced 800ms and then has
   * to round-trip: for well over a second after the rep acts, the
   * server-known item still looks base-following, and only these refs know
   * better.
   */
  const senderWriteItemRef = useRef<string | null>(null);
  const copyWriteItemRef = useRef<string | null>(null);
  const senderPickedItemRef = useRef<string | null>(null);
  const copyEditedItemRef = useRef<string | null>(null);
  const copyEdited = copyEditedItemRef.current === draftKey;

  // Round-3 (F3) — true from the synchronous top of `handleSend` until its
  // `finally`: the flush phase runs BEFORE the send POST, so neither the
  // server status (`sending`) nor the context's `isSending` (flipped in
  // sendMutation.onMutate, i.e. at POST time) covers it. State (for
  // rendering) + ref (the re-entry guard — a state alone can't stop two
  // clicks landing in one batch before the re-render).
  const [sendInProgress, setSendInProgress] = useState(false);
  const sendInProgressRef = useRef(false);

  // U2 (buttonless autosaving editor, behavior spec #4): fields stay
  // editable through BOTH `draft` and `ready` — there is no separate
  // readiness-toggle step to unlock further editing, and `ready` is no longer a user-facing
  // lock, just an internal admission state. Only an ACTIVE send — from the
  // moment Send is clicked (`sendInProgress`, round-3 F3: the flush snapshot
  // must not be editable out from under the send) through the server status
  // flip (`sending`) — or a read-only session locks the fields. `submitted`
  // is left unlocked by this same condition too — in practice unreachable in
  // the UI regardless, since a submitted item is filtered out of GET
  // /api/flow/queue's response (getQueueForOwner, src/services/flow/queue.ts)
  // and disappears from `groups` the moment the post-send refetch lands.
  //
  // Findings 1/2 (adversarial follow-up): `itemBusy` — the CONTEXT-level
  // per-item pipeline lock — joins the condition so the fields lock in
  // EVERY mounted instance of this item the instant any surface's pipeline
  // starts, not just in the instance that initiated it. That is what makes
  // "an edit landed while the save was in flight and was silently dropped
  // from it" structurally impossible: mid-save, there is nowhere left to
  // type. `sendInProgress` stays as instance-local defense in depth.
  // terra-ohe.8 (F1/DBOS durable-send migration U6) — `queued` locks exactly
  // like `sending`: it's the same "admitted into the send pipeline, no
  // longer this rep's to edit" window, just the phase before the DBOS
  // workflow actually claims the row.
  const sendGate = sendReadOnly ?? readOnly;
  const locked = item.status === 'sending' || item.status === 'queued' || readOnly || sendInProgress || itemBusy;
  const lockedTitle = readOnly
    ? 'Enroll queue is read-only while impersonating'
    : item.status === 'sending' || sendInProgress || itemBusy
      ? 'Sending…'
      : item.status === 'queued'
        ? 'Queued…'
        : undefined;

  /**
   * Codex adversarial follow-up (D24 round) — for a row that is FOLLOWING
   * the base (not `copyEdited`, not `isItemOverridden`), read subject/body
   * from the UNDEFERRED `copyPrefill` rather than `draft`. `draft` is only
   * kept in sync with the base by the live-follow effect below, which reads
   * `baseSubject`/`baseBody` through `useDeferredValue` — deliberately, so
   * N open editors can't extend a Global-config keystroke's critical path.
   * That means `draft` can lag the true staged value by one deferred React
   * commit. `currentPatch` feeds directly into the mark-ready PATCH the send
   * pipeline fires (`:1117`), so shipping straight from `draft` in that
   * window would send the PRE-edit copy — exactly the regression the
   * live-follow effect exists to prevent, reintroduced through the one path
   * that doesn't go through `draft`. A rep's own edits (`copyEdited`) or a
   * real per-item override are unaffected — `draft` is already the correct
   * source for those, deferred-value staleness never applies to them.
   */
  const sendSubject = !copyEdited && !isItemOverridden(item) ? (copyPrefill?.subject ?? draft.subject) : draft.subject;
  const sendBody = !copyEdited && !isItemOverridden(item) ? (copyPrefill?.body ?? draft.body) : draft.body;
  const currentPatch = { sender: draft.sender, subject_draft: sendSubject, body_draft: sendBody };

  /**
   * The autosave payload for THIS item given a set of draft values — the ONE
   * place the sparse patch and its per-half provenance are derived, so the
   * synchronous sender handler (D12 Codex fix #1) and the passive dirty
   * effect below can never disagree about either.
   *
   * `null` means "nothing local to write": no half is flagged, so there is no
   * PATCH to schedule (and, crucially, no empty request body to send).
   *
   * Stable per DRAFT KEY: it only reads the four refs (stable identities) and
   * `draftKey`, never render-scoped draft state — the values come in as an
   * argument precisely so this can stay out of every effect's churn. Keyed on
   * `draftKey` rather than `item.id` since D12 Codex round-2 fix #2, so a
   * completed reset re-derives provenance from scratch.
   */
  const autosavePayloadFor = useCallback(
    (values: EnrollmentItemDraft): { patch: ItemAutosavePatch; owned: ItemAutosaveOwnership } | null => {
      const senderWrite = senderWriteItemRef.current === draftKey;
      const copyWrite = copyWriteItemRef.current === draftKey;
      if (!senderWrite && !copyWrite) return null;
      return {
        patch: {
          ...(senderWrite ? { sender: values.sender } : {}),
          ...(copyWrite ? { subject_draft: values.subject, body_draft: values.body } : {}),
        },
        owned: {
          sender: senderPickedItemRef.current === draftKey,
          copy: copyEditedItemRef.current === draftKey,
        },
      };
    },
    [draftKey],
  );

  // ARIA-label identifier — see `contactDisplayIdentity`'s doc comment
  // (Fix 4, Warm Intro plan U9, R4: ~55% of warm-intro targets have no
  // email; this is the ONE shared helper every EnrollQueue component uses
  // so a null `contact_email` never renders the literal string "null").
  const contactLabel = contactDisplayIdentity(item);

  // ── Server-resolved options fields (U8 connector picker + Task 4
  // Preferred Channel, round-2 QA feedback) ───────────────────────────────
  // Selections are NOT draft state: each persists immediately (like Mark
  // ready), so there is no `useDraftState` entry for them and nothing for an
  // SSE re-render to wipe. The rendered value is always the server's — read
  // per-field, below, via `persistedValueFor(item, field.persistsTo)`
  // (module-level helper, generalized over the discriminator so this file
  // never switches on a field's `key` string).

  // Fix 5a (MAJOR bd finding): for an editable (draft OR ready — U2:
  // `!locked` is now the general "is this item still editable" test) warm_intro
  // item (`inputOptions.length > 0` is the established proxy — see the prop's
  // doc comment above), ask the context to force-refresh the cached
  // connector list if it's gone stale relative to the mirror's real hourly
  // sync cadence. Depends on `inputOptions.length` (a stable primitive, not
  // the array itself) rather than `inputOptions` directly: `inputOptions`
  // often arrives AFTER mount (the options query is async), so the effect
  // must re-check once the length flips from 0 -> N on the SAME mount, not
  // only re-run on a later item/status change. Once N is reached it stays
  // stable across a refetch (same or similar count), so this does not loop.
  useEffect(() => {
    if (locked || inputOptions.length === 0) return;
    onRefreshInputOptionsIfStale?.(item.id);
  }, [item.id, locked, inputOptions.length, onRefreshInputOptionsIfStale]);

  // Single-option sender auto-default (QA round 3, Spencer): when "Send as
  // yourself" (or any sole option) is the ONLY sender, never park the select
  // on the "Select sender…" placeholder — default to it so `itemBlockReason`
  // clears (no "Needs a sender" block) without a pointless manual pick.
  // Draft-state only (no auto-PATCH here — the value autosaves through the
  // effect below like any other field edit), unlike the connector default
  // which needs a server write immediately. Idempotent and StrictMode-safe
  // (re-running converges on the same value; no lifetime refs). Deliberate
  // consequence: re-selecting the placeholder snaps straight back — with one
  // option the placeholder is unreachable by design. Multi-option lists are
  // untouched. `senders` arrives async (GET /api/flow/queue/senders) after
  // the draft seeds, which is why seedEnrollmentItemDraft alone can't do
  // this. U2: gated on `locked` (draft OR ready both qualify now), not on
  // `item.status !== 'draft'` — a `ready` item with a since-emptied sender
  // still gets the same default.
  useEffect(() => {
    if (locked) return;
    if (senders.length !== 1) return;
    if (draft.sender.trim() !== '') return;
    // D12 Codex fix (four-ref split): a WRITE — the healed value does have to
    // reach the server, so the autosave patch must carry `sender` — but NOT
    // an owned pick. `senderPickedItemRef` stays clear, so a staged global
    // sender may still follow this row and send-time materialization may
    // still replace this value. That is the same "a heal is not a pick"
    // distinction `onSenderTouched` already encoded; it now applies to the
    // write's provenance too, not just to the touched set.
    senderWriteItemRef.current = draftKey;
    form.setDraft({ sender: senders[0].value });
  }, [locked, draftKey, senders, draft.sender, form]);

  /**
   * Idempotency ledger for the default-selection auto-PATCH: `${itemId}:${fieldKey}`
   * for every field this editor instance has already defaulted.
   *
   * Deliberately NOT reset at the top of the mount effect (CLAUDE.md
   * "StrictMode-Resilient Effects" is about refs carrying MOUNT-lifetime
   * state — `cancelled`/`mounted` flags — which go stale after the dev
   * double-invoke): this ref is the opposite, a write-once ledger whose whole
   * job is surviving `setup→cleanup→setup` so StrictMode fires ONE PATCH, not
   * two. It cannot go stale, because its keys carry the item id: a different
   * item is never blocked by another item's entry, and a genuine remount gets
   * a brand-new ref (which is correct — a remount that still sees a null
   * selection SHOULD default it again). A failed PATCH drops its key so
   * reopening the editor retries.
   */
  const autoSelectedRef = useRef<Set<string>>(new Set());

  /**
   * Fix 6 (MINOR bd finding), generalized PER-FIELD (Task 4, round-2 QA
   * feedback added a SECOND independently-defaultable field — Preferred
   * Channel — alongside the connector picker, so a single `useRef<string |
   * null>` could no longer tell "the rep just picked a connector" apart from
   * "the rep just picked a channel"). Keyed by `field.key`, a
   * `Map<string, string | null>` — presence (`.has()`) means "the rep picked
   * SOMETHING for this field on this mount" (including re-picking back to a
   * null/clear value), so unlike the single-ref version this can't confuse
   * "never picked" with "picked null".
   *
   * Guards the auto-default PATCH below against racing the rep's OWN
   * immediate manual pick: the effect fires an async PATCH for the default
   * option while the select is already interactive; if the rep picks a
   * DIFFERENT option before that request settles, the two PATCHes can
   * resolve on the server out of INITIATION order — so the auto-default's
   * write can land AFTER the rep's, silently reverting their choice.
   *
   * Captures the rep's latest pick SYNCHRONOUSLY, in the `<select>`'s
   * `onChange` below, before any network round trip. A plain
   * `useRef(new Map())` already starts fresh on every genuine mount, but
   * this is still "lifetime" state for the mount's duration (CLAUDE.md
   * "StrictMode-Resilient Effects"), so it's explicitly reset (a fresh Map,
   * not `.clear()`, matches the original single-ref's "just reassign" reset)
   * at the top of a mount effect below — the same rule `autoSelectedRef`
   * above is EXEMPT from (it's a write-once ledger meant to survive
   * StrictMode's replay), but this ref's whole job is reflecting "what did
   * the rep do just now", which must start clean on every real mount.
   */
  const manualPickRef = useRef<Map<string, string | null>>(new Map());
  useEffect(() => {
    manualPickRef.current = new Map();
  }, [item.id]);

  useEffect(() => {
    // Locked items are read-only everywhere else in this form; a background
    // write into a `ready`/`submitted` item (or under impersonation, where
    // writes are blocked server-side) would be worse than no default.
    if (locked || !onSelectInputOption) return;
    for (const field of inputOptions) {
      // Already chosen — the DB's value wins; never overwrite a rep's pick.
      // Per-field now (Task 4): checked INSIDE the loop, via the field's own
      // `persistsTo` column, not a single item-wide flag. Inlined (rather
      // than calling `persistedValueFor(item, ...)`) so the effect's own
      // dependency array can list the two columns directly instead of the
      // whole `item` object.
      const persisted = field.persistsTo === 'preferred_channel' ? item.preferred_channel : item.warm_intro_id;
      if (persisted !== null) continue;
      // Server pre-sorts connector options by `connectorScore` DESC
      // nulls-last, and Preferred Channel's options with the default first
      // (`preferredChannelOptions`) — so [0] IS the plan's default for
      // EITHER field. No client-side re-ranking (it would fork the rule).
      const first = field.options[0];
      if (!first) continue; // nothing to default (e.g. contact has zero connectors)
      const ledgerKey = `${item.id}:${field.key}`;
      if (autoSelectedRef.current.has(ledgerKey)) continue;
      autoSelectedRef.current.add(ledgerKey);
      // Fix 6, guard 1 ("before firing"): a manual pick already happened for
      // THIS FIELD on this mount — never fire the auto-default at all. The
      // ledger key is still claimed above so a later re-render doesn't retry
      // needlessly; the rep's own PATCH (fired from the `onChange` below) is
      // what ultimately persists.
      if (manualPickRef.current.has(field.key)) continue;
      void onSelectInputOption(item.id, field.key, field.persistsTo, first.value)
        .then(() => {
          // Fix 6, guard 2 ("when applying its result"): the rep picked
          // something for THIS FIELD WHILE this auto-default PATCH was in
          // flight. Re-assert their choice now — this guarantees the LAST
          // write this client issues always matches the rep's real intent,
          // regardless of which of the two PATCHes the server happened to
          // process first.
          if (!manualPickRef.current.has(field.key)) return;
          const pick = manualPickRef.current.get(field.key) ?? null;
          if (pick !== first.value) {
            void onSelectInputOption(item.id, field.key, field.persistsTo, pick).catch(() => {});
          }
        })
        .catch(() => {
          // Surfaced by the mutation's own error path; unblock a retry on the
          // next mount rather than leaving the item silently unselected forever
          // (the server-side mark-ready gate still catches it either way).
          autoSelectedRef.current.delete(ledgerKey);
        });
    }
  }, [item.id, item.warm_intro_id, item.preferred_channel, locked, inputOptions, onSelectInputOption]);

  // ── Subject/Body auto-prefill from the selected connector (Task 3,
  // round-2 QA feedback) ──────────────────────────────────────────────────
  // The rendered Subject/Body prefill rides on the SELECTED connector's own
  // option (`InputFieldOptions.options[].subject`/`.body` —
  // input-options.ts renders it per-connector because the sign-off name
  // varies). Re-applies whenever the connector changes AND the rep hasn't
  // typed since the LAST auto-apply — tracked PER FIELD (Subject and Body
  // independently), so a rep who edits Subject but leaves Body untouched
  // keeps getting Body's prefill on a later connector switch (see
  // Implementation Notes for the per-field-vs-whole-draft choice).
  const connectorField = findConnectorField(inputOptions);
  const selectedConnectorValue = connectorField ? item.warm_intro_id : null;
  const selectedConnectorOption = connectorField?.options.find(o => o.value === selectedConnectorValue);
  const prefillSubject = selectedConnectorOption?.subject;
  const prefillBody = selectedConnectorOption?.body;

  /**
   * "The values we last auto-applied", per field — mount-lifetime state
   * (CLAUDE.md "StrictMode-Resilient Effects": reset at the TOP of the
   * mount effect below, same treatment as `manualPickRef` above, not only
   * on cleanup). Distinguishes "still showing our own prefill" (safe to
   * replace on a connector switch) from "the rep typed something else"
   * (never touch again, even on a later connector switch).
   */
  const lastAutoAppliedRef = useRef<{ subject: string | null; body: string | null }>({ subject: null, body: null });
  // Keyed on `draftKey`, not `item.id` (D12 Codex round-2 fix #2): a completed
  // reset blanks the row's drafts, so the connector-driven prefill has to be
  // re-appliable afterwards. Leaving this pinned to the pre-reset value would
  // make the effect below treat the reseeded field as already-auto-applied and
  // skip it.
  useEffect(() => {
    lastAutoAppliedRef.current = { subject: null, body: null };
  }, [draftKey]);

  // Local bindings (not `form.setDraft`/`form.reseed` inline) so effects'
  // dep arrays can list the plain identifiers, which the exhaustive-deps
  // lint rule tracks precisely — `useDraftState` returns a fresh `form`
  // object every render, so depending on the whole object would re-run an
  // effect on every unrelated re-render. `setDraft`/`reseed` are themselves
  // `useCallback([], ...)`-stable (see useDraftState.ts), so this actually
  // fixes identity, it doesn't just relabel the problem.
  const { setDraft, reseed } = form;

  useEffect(() => {
    // Copy fields don't even render when `!hasEditableCopy` — a prefill
    // into an invisible/inert draft would just be a silent later surprise
    // if the play type ever re-enabled copy. Locked items are read-only.
    if (locked || !hasEditableCopy) return;
    if (prefillSubject !== undefined) {
      const current = draft.subject;
      const untouched = current === '' || current === lastAutoAppliedRef.current.subject;
      if (untouched) {
        if (current !== prefillSubject) {
          // D12 Codex fix (four-ref split): the connector's copy is a WRITE
          // (it must persist) but not the REP's own typing, so
          // `copyEditedItemRef` deliberately stays clear — this must neither
          // stop the base-copy live-follow nor claim copy ownership at
          // send-time materialization.
          copyWriteItemRef.current = draftKey;
          setDraft({ subject: prefillSubject });
        }
        lastAutoAppliedRef.current.subject = prefillSubject;
      }
    }
    if (prefillBody !== undefined) {
      const current = draft.body;
      const untouched = current === '' || current === lastAutoAppliedRef.current.body;
      if (untouched) {
        if (current !== prefillBody) {
          copyWriteItemRef.current = draftKey;
          setDraft({ body: prefillBody });
        }
        lastAutoAppliedRef.current.body = prefillBody;
      }
    }
  }, [draftKey, prefillSubject, prefillBody, locked, hasEditableCopy, draft.subject, draft.body, setDraft]);

  // ── D8 Part B point 3 — LIVE-FOLLOW the play's staged base copy ─────────
  // Codex adversarial review, regression #1. `copyPrefill` already updates
  // live as the rep types into the card's Global config (`PlayQueueCard`
  // recomputes it straight off the staged store, no network), but
  // `useDraftState` applies a seed exactly ONCE per entity identity and
  // deliberately ignores later seed changes for an already-mounted item —
  // correct for a rep's own typed edits, and wrong here. Without this
  // effect an editor the rep opened BEFORE touching Global config kept
  // rendering the pre-keystroke copy, and its own "Send 1" shipped it.
  //
  // D24 (qa-round3 plan, fixes 2+3) restates the TIMING this comment used to
  // claim as "immediately": `BaseCopyEditor` now buffers keystrokes and
  // commits to the staged store on a ~200ms idle debounce (or on blur, or on
  // unmount), and the deferral below schedules this reseed at low priority.
  // So an open editor follows a base keystroke WITHIN ~200ms rather than
  // synchronously. The correctness guarantee is unchanged and is what the
  // regression was actually about: the follow is not gated on a remount, and
  // Send can never ship the pre-keystroke value — blur commits before the
  // Send button's click, and `buildStagedSnapshot` reads the store at call
  // time (`readStagedConfig`), not from a render snapshot.
  //
  // Split into `baseSubject`/`baseBody` scalars rather than depending on
  // `copyPrefill` itself: that object is rebuilt on every parent render (this
  // editor re-renders on every unrelated SSE event — CLAUDE.md "Draft State
  // Under SSE"), so depending on it would re-run this on every render for
  // nothing.
  //
  // D24 (qa-round3 plan, fix 3) — DEFERRED. This effect is the second render
  // pass every open editor pays per base-copy change: it reseeds local draft
  // state, which re-renders this editor's subtree again. Not a loop (the
  // `next.subject === draft.subject` early return converges after one pass,
  // and `reseed` deliberately doesn't mark dirty so no PATCH fires) — but it
  // doubles the cost, once per open editor.
  //
  // `useDeferredValue` makes the follow eventually-consistent: React commits
  // the urgent render (the rep's caret in Global config) first and schedules
  // the reseed at low priority, so N open editors can no longer extend the
  // keystroke's critical path. Correct BECAUSE following the base is a
  // display change with no write attached — there is nothing here whose
  // ordering a rep or the server can observe. It is only ever read by the
  // effect below; every other consumer of `copyPrefill` (the mount seed,
  // send-time materialization) reads the undeferred value.
  const baseSubject = useDeferredValue(copyPrefill?.subject ?? null);
  const baseBody = useDeferredValue(copyPrefill?.body ?? null);
  useEffect(() => {
    // Copy fields don't render at all without `hasEditableCopy`, and a
    // locked item is read-only everywhere else in this form.
    if (locked || !hasEditableCopy) return;
    // The precise scope of the fix, and the reason this is not a blanket
    // "always reseed": a row with its own persisted draft (`isItemOverridden`)
    // or one the rep is mid-edit on (`copyEdited`) has a REAL per-item
    // override, and reseeding it out from under the rep is exactly the bug
    // `useDraftState`'s seed-ignoring contract exists to prevent. That
    // contract is left untouched — this only covers the base-following case.
    //
    // D12 Codex fix #3: the gate is `copyEdited` — the rep's own TYPING —
    // never the old catch-all `repEdited`, which a mere SENDER pick also set.
    // A rep who picked a sender has said nothing about the copy, so the row
    // must keep following later global copy edits; under the old flag the
    // pick silently detached it from the base forever.
    if (copyEdited || isItemOverridden(item)) return;
    const next = seedEnrollmentItemDraft(item, { subject: baseSubject, body: baseBody });
    if (next.subject === draft.subject && next.body === draft.body) return;
    // `reseed`, never `setDraft`: it moves `pristine` alongside `draft`, so
    // `isDirty` stays false and the autosave effect below does NOT fire.
    // Following the base is a DISPLAY change; writing it per base keystroke
    // would silently convert every open row into a manual override — the
    // same failure mode as regression #3, arrived at from the other side.
    // `sender` is carried through untouched (the base copy has no sender
    // rung until D12).
    reseed({ sender: draft.sender, subject: next.subject, body: next.body });
  }, [item, locked, hasEditableCopy, copyEdited, baseSubject, baseBody, draft.sender, draft.subject, draft.body, reseed]);

  // ── D12 point 4 — LIVE-FOLLOW the play's staged global SENDER ────────────
  // The sender half of the effect above, and deliberately its own effect
  // rather than another clause inside it, for three reasons:
  //   - it is NOT gated on `hasEditableCopy` (every play type has a sender,
  //     including the ones that resolve their copy server-side);
  //   - it is NOT gated on `repEdited`/`isItemOverridden` either. Those are
  //     COPY signals: a rep who typed a bespoke subject has said nothing
  //     about who it sends as, and under the precedence model
  //     ("if i edit the global then all the children should be overwritten,
  //     then if i edit a child it will take its own priority of config") only
  //     a hand-picked SENDER pins the sender. That pin arrives here as
  //     `senderPrefill === null` — the card applies it — which is the single
  //     condition below, so a rep's own pick can never be overwritten by a
  //     later global pick: `onSenderChange` marks the row touched
  //     SYNCHRONOUSLY, in the same event, so the very next render already
  //     sees `null` here;
  //   - `useDraftState` applies a seed exactly once per entity identity, so
  //     the mount seed above cannot cover an editor the rep opened BEFORE
  //     touching Global config (the same gap Codex found for copy — D8
  //     regression #1).
  // `reseed`, never `setDraft` (identical reasoning to the copy effect):
  // following the base is a DISPLAY change, and writing it per global pick
  // would silently convert every open row into a manual override. Send-time
  // resolution now happens server-side (U2) off the POST's `staged` field
  // (`buildStagedSnapshot`) — this editor writes nothing for it at all.
  useEffect(() => {
    if (locked) return;
    if (senderPrefill === null) return;
    if (draft.sender === senderPrefill) return;
    reseed({ sender: senderPrefill, subject: draft.subject, body: draft.body });
  }, [locked, senderPrefill, draft.sender, draft.subject, draft.body, reseed]);

  // U2 (buttonless autosaving editor, behavior spec #1) — autosave: whenever
  // the draft has an un-persisted edit (`form.isDirty`, useDraftState's own
  // shallow-equal-vs-pristine flag), schedule the debounced PATCH, then
  // IMMEDIATELY `reseed` (pristine := draft) so `isDirty` clears again.
  //
  // The reseed is load-bearing, not cosmetic: `EnrollQueueContext`'s mutation
  // hooks (`useMutation`) return a brand-new result object on every render
  // (TanStack Query does not memoize it — confirmed in
  // node_modules/@tanstack/react-query), so EVERY `useCallback` derived from
  // it (`autosavePatch`, `flushAutosave`, `patchItem`, ...) is ALSO a new
  // function identity on every provider render, not just when something
  // mutation-related changed. Without the reseed, `isDirty` stays `true`
  // forever after the first keystroke (nothing else clears it), so ANY
  // re-render — including the one this very effect's PATCH triggers via
  // query invalidation — would re-satisfy `!locked && form.isDirty`, call
  // `onAutosave` again, invalidate again, re-render again: an infinite loop
  // (confirmed by a real "Maximum update depth exceeded" crash before this
  // reseed was added). Reseeding makes `isDirty` track ONLY genuinely NEW
  // keystrokes since the last scheduled autosave, which is also the correct
  // contract for "exactly one settled PATCH per pause" (behavior spec #1) —
  // the debounce coalescing itself still happens in
  // `EnrollQueueContext.autosavePatch`, this just stops the effect from
  // re-firing for a change that was already handed off.
  //
  // Gated on `locked` so a `sending`/read-only editor never fires a write
  // (mirrors every other write-effect in this file). NOT gated on
  // `hasEditableCopy`: the Sender field autosaves the same way even for a
  // play with no rep-editable copy (generate_message) — the server simply
  // persists whatever the item's stored value already was for a field the
  // editor never showed.
  useEffect(() => {
    if (locked || !form.isDirty) return;
    // D12 Codex fix (findings #1–#3): the payload is SPARSE and provenance-
    // carrying, derived through the one shared `autosavePayloadFor` so this
    // effect and the sender handler below can never produce different
    // patches for the same state. `null` means no half is flagged for a
    // local write — reachable only via a reseed race, and it must never
    // become an empty PATCH body.
    const payload = autosavePayloadFor(draft);
    if (payload) onAutosave(item.id, payload.patch, payload.owned);
    reseed(draft);
  }, [item.id, locked, form.isDirty, draft, autosavePayloadFor, onAutosave, reseed]);

  /**
   * U2: flush any pending autosave for THIS item when the editor unmounts OR
   * switches to a different item — "flushed on Send, unmount, and item
   * switch" (bead's Do-NOT-create list: no per-field timers, exactly one
   * flush point per lifecycle edge). Safe by construction against the
   * classic stale-closure trap a `currentPatch`-capturing ref would hit here:
   * `onFlushSave` reads whatever `EnrollQueueContext.autosavePatch` last
   * scheduled for `item.id` from ITS OWN map, not from a value threaded back
   * out of this component (see EnrollQueueContext.tsx's `autosaveEntriesRef`
   * doc comment) — so the id captured by this effect's cleanup is all that's
   * needed, regardless of when the flush actually executes.
   *
   * P1-LOOP (plan 2026-08-19-004, U4): the callback is read through an
   * always-current ref and the effect's dep list is `[item.id]` ONLY —
   * cleanup therefore means "unmount or item switch", never "the provider
   * re-rendered and handed down a new function identity". `onFlushSave`
   * derives from react-query's `useMutation` result, which is a brand-new
   * object on EVERY provider render; with `onFlushSave` in the dep list, a
   * FAILED flush re-rendered the provider (mutation state), the cleanup
   * re-fired, the flush retried the retained patch, failed again — an
   * unbounded failure→re-render→cleanup-flush→retry loop with no backoff.
   *
   * Fire-and-forget with an explicit no-op catch: there is no send to abort
   * at unmount, and a bare `void` on a rejecting promise inside a cleanup
   * function surfaces as an unhandled rejection.
   */
  const onFlushSaveRef = useRef(onFlushSave);
  useEffect(() => { onFlushSaveRef.current = onFlushSave; });
  useEffect(() => {
    const flushItemId = item.id;
    return () => { void onFlushSaveRef.current(flushItemId).catch(() => {}); };
  }, [item.id]);

  // U2 (behavior spec #6) — a rejected mark-ready PATCH (e.g. the contact
  // lost its email, `InvalidPatchError`/`SenderRequiredError`) must render
  // inline and leave the item exactly where it was — never let the exception
  // bubble past this handler into an unhandled rejection. Reset on item
  // switch so a stale error from a PREVIOUS item never bleeds into a freshly
  // opened one.
  const [sendError, setSendError] = useState<string | null>(null);
  // Reset inline during render (react.dev's "adjust state during render"
  // pattern) rather than in a useEffect — an effect whose whole body is a
  // single derived setState call is exactly what `terra/no-derived-state-
  // in-effect` disallows, and this form also avoids the one-render flash of
  // a stale error the effect version could show before it fired.
  const prevItemIdRef = useRef(item.id);
  if (prevItemIdRef.current !== item.id) {
    prevItemIdRef.current = item.id;
    setSendError(null);
  }

  /**
   * Finding #5 defense in depth: Remove/Close are disabled while the item is
   * locked, so an unmount mid-pipeline shouldn't happen — but if one does
   * anyway (e.g. a play card collapsing over an in-flight send), the
   * async chain below must not call this unmounted instance's state setters.
   * Reset at the TOP of the mount effect, not only in cleanup (CLAUDE.md
   * "StrictMode-Resilient Effects" — a cleanup-only flag is left stuck
   * `true` after the dev double-invoke and on real remounts).
   */
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  const handleSend = async () => {
    // Round-3 (F3) — synchronous re-entry guard: checked-and-set before the
    // first await, so a double-click can never launch a second concurrent
    // flush/send pipeline (the second click lands while the first is still
    // in its flush phase, where nothing else is locked yet).
    if (sendInProgressRef.current) return;
    // Findings 1/2/5 — the CONTEXT lock: refuses when ANY surface already
    // has a pipeline running for this item (this instance's own ref above
    // can't know that), and marks the item busy for every mounted instance
    // before the first await. Claimed here, released unconditionally in the
    // `finally` below.
    if (!onBeginSendPipeline([item.id])) return;
    // Round-5 adversarial follow-up (P2): `try` starts IMMEDIATELY after a
    // successful claim — no statement between claim and `finally` may be
    // allowed to throw uncaught, or the context lock leaks forever for this
    // item. The ref/state writes below are not expected to throw, but the
    // whole point of the guarantee is not relying on that.
    try {
      sendInProgressRef.current = true;
      setSendInProgress(true);
      setSendError(null);
      // terra-ohe.7 (F1/DBOS durable-send migration U6) — Steps 1, 1b, 2, 3
      // ONLY: there is no materialization step any more (the server resolves
      // the rep's staged base copy/sender itself, at claim time, off the
      // `staged` field the send POST carries — see
      // `EnrollQueueContext.buildStagedSnapshot`/`sendItems`) and therefore no
      // compensating rollback either.
      //
      // Step 1 (behavior spec #2): flush any pending debounced edit so the
      // server has the rep's latest text before the ready transition below.
      await onFlushSave(item.id);
      // Step 1b (basic save-and-send, plan 2026-08-19-004): the SAVE phase
      // for the gong_flow per-step Subject/Copy overrides, which live in the
      // context's local draft store and are written NOWHERE else — Send is
      // the one write moment. Must be awaited HERE, between the item flush
      // and the `ready` flip: the server's `assertOwnedEditableItem`
      // (`src/services/flow/step-drafts.ts:96-107`) rejects a step-override
      // write with `STEP_DRAFT_ITEM_IMMUTABLE` the moment the item is no
      // longer editable, so a save moved below Step 2 would ship stale copy
      // to Gong AND leave a "Failed to save — retry" on an already-sent
      // item. A rejection propagates to the catch below, which aborts the
      // send — a failed save must never be papered over by sending the old
      // copy.
      await onSaveStepOverrides(item.id);
      // Step 2: the mark-ready transition — the last write gate before send
      // admission (SenderRequiredError, WarmIntroRequiredError, tombstone
      // checks all live here server-side, see queue.ts patchQueueItem).
      await onSetStatus(item.id, 'ready', currentPatch);
      // Step 3: only reached once both prior PATCHes have resolved. The
      // server may still SKIP this item (a successful 202 whose body says it
      // did not go out) — there is nothing to compensate for that any more:
      // no client materialization ever committed anything onto the row.
      await onSend?.(item.id);
    } catch (err) {
      if (!unmountedRef.current) setSendError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      // Cleared unconditionally: on failure the rep gets an editable item
      // back with `sendError` rendered inline; on success the context's
      // `isSending` (flipped in sendMutation.onMutate, before `onSend`
      // resolves) keeps the Send button disabled until the delivery poll
      // settles, and the post-send refetch removes the row. Fields briefly
      // re-enable between the POST resolving and that refetch — the
      // pre-round-3 window, accepted (Decision #6: the isSending/server-
      // status locks take over from POST time onward).
      //
      // The context lock is released FIRST and unconditionally — it is
      // shared state other surfaces gate on, so it must never leak past
      // this pipeline regardless of how it ended (success, failure, or an
      // unmount mid-flight — the context callback identity is stable and
      // safe to call from an unmounted instance's closure).
      onEndSendPipeline([item.id]);
      sendInProgressRef.current = false;
      if (!unmountedRef.current) setSendInProgress(false);
    }
  };

  // U2 (behavior spec #5) — itemBlockReason is the ONE client-visible
  // send-eligibility check (unassigned play, empty sender, sending/sent
  // states), shared with `PlayQueueCard`'s group sendability (via
  // `deriveGroupSendability`) so the copy can't drift between the row editor
  // and the card. Evaluated against the item's server
  // fields with `sender` overridden by the LIVE draft (mirrors the removed
  // Mark Ready button's old `senderMissing = draft.sender...` check): the
  // rep picking/typing a sender must clear the block IMMEDIATELY, not after
  // the debounced autosave round-trips and the item prop catches up — that
  // would gate Send on network latency the rep has no visibility into.
  // `sending` (the client-side in-flight belt — a send this component just
  // fired, before the server's status even flips) and `!onSend` (no send
  // path wired at all) are additional, purely-client conditions layered on
  // top.
  // Round-3 (F3): `sendInProgress` joins the belt so the button disables the
  // instant Send is clicked — `sending` only covers POST-onward. Findings
  // 1/2: `itemBusy` joins too, so a pipeline started from ANY surface
  // disables this instance's Send (the begin-pipeline refusal in handleSend
  // is the guard; this is its visible state).
  const blockReason = itemBlockReason({ ...item, sender: draft.sender }, sendGate);
  const sendDisabled = !!blockReason || !onSend || !!sending || sendInProgress || itemBusy;
  const sendTitle = blockReason ?? (sending || sendInProgress || itemBusy ? 'Sending…' : undefined);

  return (
    <div className={renderPreview ? 'grid gap-4 md:grid-cols-[1.6fr_1fr]' : ''}>
      <div>
        {!hideWarningBadges && (
          <WarningBadges itemId={item.id} contactEmail={item.contact_email} suppressKinds={SUPPRESSED_WARNING_KINDS} />
        )}
        <RecentSendsBar itemId={item.id} />
        {/* D8 regression #1 — the field handlers below record their local
            write SYNCHRONOUSLY, ahead of the state update, so the live-follow
            effects above see it from the very next render (the debounced
            autosave won't make `item.*_draft`/`item.sender` reflect it for
            well over a second).
            D12 Codex fix #3 — but PER HALF, not "any field counts": the copy
            handlers set the copy refs and the sender handler sets the sender
            refs, and only the copy refs gate the copy live-follow. See the
            four-ref doc comment at the top of the component. */}
        <ItemFields
          item={item}
          draft={draft}
          locked={locked}
          lockedTitle={lockedTitle}
          contactLabel={contactLabel}
          senders={senders}
          inputOptions={inputOptions}
          hasEditableCopy={hasEditableCopy}
          copyRequired={copyRequired}
          // D12 point 4 — a sender PICK also marks the row sender-touched,
          // synchronously and before the state update, so the play's staged
          // global sender stops re-following it from the very next render
          // (the live-follow effect above reads the resulting `null`
          // `senderPrefill`). The single-option auto-default effect above
          // deliberately does NOT go through here.
          onSenderChange={value => {
            // D12 Codex fix #8 — re-activating the option that is ALREADY
            // selected is not a pick. Base UI's `Select` fires
            // `onValueChange` on every activation, including a no-op
            // re-selection, so without this guard a rep who merely opens a
            // single-sender play's picker and clicks the one option shown
            // pinned that row out of the play's staged sender for good.
            if (value === draft.sender) return;
            // D12 Codex fix #1 — marking the row touched and ENQUEUING the
            // picked value are ONE synchronous unit here. Marking touched in
            // this handler while letting the VALUE reach the shared entry
            // machinery only via the later dirty-effect left a same-tick
            // window where a materialization saw either a stale "untouched"
            // row (and overwrote this pick) or a touched row with nothing
            // queued (and materialized nothing at all for it).
            senderWriteItemRef.current = draftKey;
            senderPickedItemRef.current = draftKey;
            const next = { ...draft, sender: value };
            onSenderTouched?.(item.id);
            const payload = autosavePayloadFor(next);
            if (payload) onAutosave(item.id, payload.patch, payload.owned);
            // `reseed`, not `setDraft`: the write above already handed this
            // value off, so `isDirty` must NOT go true — otherwise the
            // passive effect fires a second, identical autosave for the same
            // pick. Same "schedule then mark handed-off" shape that effect
            // uses itself.
            form.reseed(next);
          }}
          // The copy handlers set BOTH copy refs (a rep-owned local write)
          // and neither sender ref — see the four-ref doc comment: typing a
          // bespoke subject says nothing about who the row sends as.
          onSubjectChange={value => { copyWriteItemRef.current = draftKey; copyEditedItemRef.current = draftKey; form.setDraft({ subject: value }); }}
          onBodyChange={value => { copyWriteItemRef.current = draftKey; copyEditedItemRef.current = draftKey; form.setDraft({ body: value }); }}
          onInputOptionChange={(field, value) => {
            // Fix 6 (MINOR bd finding): capture the pick SYNCHRONOUSLY —
            // before the PATCH below even starts — so the auto-default
            // effect's two race guards always see it in time, no matter how
            // the two requests' responses end up interleaving. Per-field
            // (Task 4): keyed by `field.key`.
            manualPickRef.current.set(field.key, value);
            // Fix 5a (MAJOR bd finding): `.catch()` is required here, not
            // optional cleanup — a 400 (the picked connector vanished
            // server-side) is now an EXPECTED outcome this fix specifically
            // triggers healing for (see `selectInputOption`'s
            // invalidate-on-400 in EnrollQueueContext.tsx), so it must not
            // surface as an unhandled promise rejection. The global
            // MutationCache error toast (main.tsx) still fires regardless —
            // this just acknowledges the rejection locally.
            void onSelectInputOption?.(item.id, field.key, field.persistsTo, value).catch(() => {});
          }}
        />

        {/* U13 — gong_flow items only: portable step-override editor +
            cool-off checkbox + owner note, beneath the shared field
            cluster (locked prototype's `gongStepsHtml`). The step callbacks
            are required together (no partial mount) — a caller passing
            `gongFlowId` is expected to have wired them all, since
            `EnrollQueueContext` exposes them as one cohesive set. */}
        {gongFlowId != null && onEditStepOverride && onResetStepOverride && stepOverrideDraftFor && stepSaveErrorFor && subscribeStepOverrides && onToggleCoolOff && (
          /* U2 (V2 rail, D25, additive-only) — `stepsSection`, when passed,
             replaces this mount entirely so the V2 rail can render its own
             gong-flow step-editor UI in place of `GongFlowStepsSection`.
             Left inside the SAME pre-existing guard above (rather than
             adding a `&& !stepsSection` to it) so this diff never touches an
             existing line — the parenthesis below just wraps the untouched
             `<GongFlowStepsSection>` mount as the ternary's else-branch. */
          stepsSection ? stepsSection({ itemId: item.id, gongFlowId, bodyFormat, locked, lockedTitle, batchStepCopy }) : (
          <GongFlowStepsSection
            itemId={item.id}
            flowId={gongFlowId}
            overrides={item.step_drafts}
            locked={locked}
            lockedTitle={lockedTitle}
            onEditStepOverride={onEditStepOverride}
            onResetStepOverride={onResetStepOverride}
            stepOverrideDraftFor={stepOverrideDraftFor}
            stepSaveErrorFor={stepSaveErrorFor}
            subscribeStepOverrides={subscribeStepOverrides}
            batchStepCopy={batchStepCopy}
            coolOffOverride={item.cool_off_override}
            onToggleCoolOff={onToggleCoolOff}
            actingRepEmail={actingRepEmail}
            bodyFormat={bodyFormat}
          />
          )
        )}

        {/* M5: a prior send FAILED — the item was reverted to `ready` and the
            reason persisted. Surface it so the rep isn't retrying blind. */}
        {item.last_send_error && (
          <div
            role="alert"
            className="mt-2 rounded-md bg-bg-red-light px-2 py-1.5 text-xs text-content-danger"
          >
            Last send failed: {item.last_send_error}
          </div>
        )}

        {/* U2 (behavior spec #6) — a rejected mark-ready PATCH from THIS
            Send click, rendered verbatim (see EnrollQueueContext.tsx's
            patchMutation, which now surfaces the server's `title` rather
            than a generic status-code string). Distinct from
            `last_send_error` above: that one is a stored, server-persisted
            field describing a FAILED SEND from a previous attempt; this one
            is local, ephemeral state for a send that never even reached the
            send step because the ready transition itself was rejected. */}
        {sendError && (
          <div
            role="alert"
            className="mt-2 rounded-md bg-bg-red-light px-2 py-1.5 text-xs text-content-danger"
          >
            {sendError}
          </div>
        )}

        {/* U2 (V2 rail, D25, additive-only) — `hideSendButton` lets a caller
            (the V2 rail's `LeadRow`) hide this built-in Send affordance and
            render its own. Wrapped without re-indenting the untouched block
            below, so this diff stays additions-only. */}
        {!hideSendButton && (
        <div className="mt-2 flex items-start gap-2">
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => { void handleSend(); }}
              disabled={sendDisabled}
              title={sendTitle}
              aria-label={`${connectorField ? 'Request' : 'Send'} 1 to ${contactLabel}`}
              className="rounded-md bg-content-action px-2 py-1 text-xs text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {sending || sendInProgress || itemBusy ? 'Sending…' : connectorField ? 'Request 1' : 'Send 1'}
            </button>
            {/* connectorField (a warm_intro_id-persisting input option) is
                warm_intro-only — the button label switches to "Request" here
                because the connector, not Terra, does the actual send; this
                note makes that explicit instead of reading like a direct
                email dispatch. */}
            {connectorField && (
              <span className="text-xs text-content-tertiary">
                Sends a multithread request
              </span>
            )}
          </div>
        </div>
        )}
      </div>
      {renderPreview?.(draft)}
    </div>
  );
}
